import type { CrosspostPost, CrosspostPostStatus, SocialPlatform } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  Facebook,
  ImageIcon,
  Instagram,
  Link2,
  Loader2,
  type LucideIcon,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useCrosspostPosts,
  useDeleteCrosspostPost,
  useRefreshCrosspostPosts,
} from '~/entities/crossposting/model/use-crosspost-posts'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

const PLATFORM_META: Record<SocialPlatform, { label: () => string; icon: LucideIcon }> = {
  facebook: { label: m.crosspostingFacebook, icon: Facebook },
  instagram: { label: m.crosspostingInstagram, icon: Instagram },
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

const STATUS_META: Record<
  CrosspostPostStatus,
  { label: () => string; variant: BadgeVariant; icon: LucideIcon }
> = {
  draft: { label: m.crosspostingStatusDraft, variant: 'outline', icon: Clock },
  scheduled: { label: m.crosspostingStatusScheduled, variant: 'secondary', icon: Clock },
  processing: { label: m.crosspostingStatusProcessing, variant: 'secondary', icon: Loader2 },
  retrying: { label: m.crosspostingStatusRetrying, variant: 'secondary', icon: RefreshCw },
  review: { label: m.crosspostingStatusReview, variant: 'secondary', icon: Clock },
  posted: { label: m.crosspostingStatusPosted, variant: 'default', icon: CheckCircle2 },
  error: { label: m.crosspostingStatusError, variant: 'destructive', icon: AlertCircle },
  deleted: { label: m.crosspostingStatusDeleted, variant: 'outline', icon: Trash2 },
}

const TYPE_LABEL: Record<CrosspostPost['type'], () => string> = {
  post: m.crosspostingTypePost,
  story: m.crosspostingTypeStory,
  reel: m.crosspostingTypeReel,
}

const dateFormatter = new Intl.DateTimeFormat('default', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function PostThumbnail({ post }: { post: CrosspostPost }) {
  const [failed, setFailed] = useState(false)

  if (post.thumbnailUrl && !failed) {
    return (
      <img
        src={post.thumbnailUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-24 w-24 shrink-0 rounded-md border object-cover sm:h-28 sm:w-28"
      />
    )
  }

  return (
    <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-muted/40 text-muted-foreground sm:h-28 sm:w-28">
      <ImageIcon className="h-5 w-5" />
      {post.mediaCount > 0 && <span className="text-xs tabular-nums">{post.mediaCount}</span>}
    </div>
  )
}

function PostCard({ post, onDelete }: { post: CrosspostPost; onDelete: () => void }) {
  const platform = PLATFORM_META[post.platform]
  const PlatformIcon = platform.icon
  const status = STATUS_META[post.status]
  const StatusIcon = status.icon
  const isPosted = post.status === 'posted'

  return (
    <Card className="flex gap-4 p-4">
      <PostThumbnail post={post} />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <PlatformIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="font-medium text-sm">{platform.label()}</span>
            <span className="text-muted-foreground text-xs">· {TYPE_LABEL[post.type]()}</span>
          </div>
          <Badge variant={status.variant} className="shrink-0 gap-1">
            <StatusIcon className={cn('h-3 w-3', post.status === 'processing' && 'animate-spin')} />
            {status.label()}
          </Badge>
        </div>

        {post.text ? (
          <p className="line-clamp-3 whitespace-pre-wrap text-muted-foreground text-sm">
            {post.text}
          </p>
        ) : (
          <p className="truncate text-muted-foreground text-sm italic">{post.title}</p>
        )}

        {post.link && (
          <a
            href={post.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
          >
            <Link2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{post.link}</span>
          </a>
        )}

        {post.error && <p className="text-destructive text-xs">{post.error}</p>}

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-1 text-muted-foreground text-xs">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              {isPosted && post.postedAt
                ? m.crosspostingPostedAt({ date: dateFormatter.format(new Date(post.postedAt)) })
                : m.crosspostingCreatedAt({
                    date: dateFormatter.format(new Date(post.createdAt)),
                  })}
            </span>
            {post.mediaCount > 0 && (
              <span className="flex items-center gap-1">
                <ImageIcon className="h-3.5 w-3.5" />
                {post.mediaCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {post.permalink && (
              <Button asChild variant="ghost" size="sm" className="h-7">
                <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  {m.crosspostingOpenPost()}
                </a>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="-mr-2 h-7 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="sr-only">{m.crosspostingDeletePost()}</span>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

export function CrosspostingPostsOverview({ listingId }: { listingId: string }) {
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useCrosspostPosts(
    businessId,
    listingId,
  )
  const { mutate: refreshPosts, isPending: isRefreshing } = useRefreshCrosspostPosts(
    businessId,
    listingId,
  )
  const { mutate: deletePost, isPending: isDeleting } = useDeleteCrosspostPost(
    businessId,
    listingId,
  )
  const [pendingDelete, setPendingDelete] = useState<CrosspostPost | null>(null)

  const seen = new Set<string>()
  const posts = (data?.pages.flatMap((page) => page.posts) ?? []).filter((post) => {
    if (seen.has(post.id)) return false
    seen.add(post.id)
    return true
  })

  const backLink = (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <Link to="/dashboard/crossposting/$listingId" params={{ listingId }}>
        <ArrowLeft className="h-4 w-4" />
        {m.crosspostingBackToTools()}
      </Link>
    </Button>
  )

  if (isBusinessLoading || isLoading) {
    return (
      <div className="space-y-6">
        {backLink}
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {backLink}

      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title={m.crosspostingOverviewTitle()}
          description={m.crosspostingOverviewDescription()}
        />
        <Button variant="outline" size="sm" onClick={() => refreshPosts()} disabled={isRefreshing}>
          <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          {m.crosspostingRefresh()}
        </Button>
      </div>

      {posts.length === 0 ? (
        <Card className="flex flex-col items-start gap-1 p-6">
          <p className="font-medium text-sm">{m.crosspostingOverviewEmpty()}</p>
          <p className="text-muted-foreground text-sm">{m.crosspostingOverviewEmptyHint()}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onDelete={() => setPendingDelete(post)} />
          ))}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {m.crosspostingLoadMore()}
              </Button>
            </div>
          )}
        </div>
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.crosspostingDeletePost()}</AlertDialogTitle>
            <AlertDialogDescription>{m.crosspostingDeletePostDescription()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={() => {
                if (!pendingDelete) return
                deletePost(pendingDelete.id)
                setPendingDelete(null)
              }}
            >
              {m.crosspostingDeletePost()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
